import { FetchPolicy } from 'apollo-client'
import debounce from 'lodash.debounce'
import React from 'react'
import { Helmet } from 'react-helmet'
import styled, {
  FOOTER_HEIGHT,
  MOBILE_FOOTER_HEIGHT,
  MQ_TABLET,
} from '../../constants/theme'
import { useCardStack } from '../../hooks/useCardStack'
import { usePagingSearchQuery } from '../../hooks/usePagingSearchQuery'
import { scrollToPosition } from '../../lib/scroll'
import { ContentLoader } from '../elements/Loader'

export const PageTitle = styled.h1(
  {
    ...MQ_TABLET({
      display: 'none',
    }),
    fontSize: 20,
    fontWeight: 'bold',
    height: 50,
    padding: '10px 50px',
  },
  ({ theme }) => ({
    backgroundColor: theme.primaryDark,
    color: theme.textLight,
  }),
)

// there's a bit of magic in here when the scroll area is inverted we use
// `scaleY(-1)` to flip the messages to be ordered correctly so properties that
// are `top` are actually `bottom` and vice versa
export const ScrollableArea = styled.div<{
  height?: string | number
  isInverted?: boolean
}>(
  {
    '.modal-open &': {
      overflow: 'hidden',
    },
    maxWidth: 1320,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  ({ height, isInverted }) => {
    let newHeight = height && !isInverted ? height : '100%'
    return {
      ...MQ_TABLET({
        height:
          newHeight > 0
            ? `calc(100% - ${Number(newHeight) + FOOTER_HEIGHT}px)`
            : newHeight,
      }),
      '& > .PaddedContent > *': {
        transform: isInverted ? 'scaleY(-1)' : 'none',
      },
      height:
        newHeight > 0
          ? `calc(100% - ${Number(newHeight) + MOBILE_FOOTER_HEIGHT}px)`
          : newHeight,
      '&::after': {
        content: '""',
        padding: isInverted ? 0 : `${MOBILE_FOOTER_HEIGHT / 2}px`,
      },
      transform: isInverted ? 'scaleY(-1)' : 'none',
    }
  },
)

export const PaddedContent = styled.div<{ isInverted?: boolean }>(
  {},
  ({ isInverted, theme }) => ({
    ...MQ_TABLET({
      padding: `0 ${theme.padding}px`,
      paddingBottom: isInverted ? 0 : theme.padding,
    }),
    padding: `0 ${theme.paddingMobile}px`,
    paddingBottom: isInverted ? 0 : theme.paddingMobile,
  }),
)

interface Props {
  children: React.ReactNode
  title: string
}

const ScrollableContent = React.memo<Props>(({ children, title }) => (
  <React.Fragment>
    <Helmet
      title={`${(window as any).CMD.config.tenant.page_title} - ${title}`}
    />
    <PageTitle>{title}</PageTitle>
    <ScrollableArea className={`${title.replace(/\W|\s/g, '')}Page`}>
      <PaddedContent>{children}</PaddedContent>
    </ScrollableArea>
  </React.Fragment>
))

export const PagingScrollableArea = React.memo<any>(
  ({
    children,
    className,
    controlOffset,
    isInverted,
    loadNextPage,
    onScroll,
    scrollDivProps,
    scrollToTop,
  }) => {
    const containerRef = React.useRef<HTMLDivElement>(null!)
    const asStacked = useCardStack(containerRef)
    const [isPageLoading, setIsPageLoading] = React.useState(false)
    const debouncedScrollHandler = debounce(e => {
      const { offsetHeight, scrollHeight, scrollTop } = e.target
      const shouldPage =
        loadNextPage && offsetHeight + scrollTop + 10 >= scrollHeight
      if (shouldPage && !isPageLoading) {
        setIsPageLoading(true)
        loadNextPage().then(() => setIsPageLoading(false))
      }
      if (onScroll) {
        onScroll(e)
      }
    }, 100)
    const handleScroll = React.useCallback(
      e => {
        e.persist()
        debouncedScrollHandler(e)
      },
      [debouncedScrollHandler],
    )
    React.useEffect(() => {
      if (scrollToTop && containerRef.current) {
        scrollToPosition(0, 0, { el: containerRef.current, duration: 666 })
      }
      return () => {
        debouncedScrollHandler.cancel()
      }
    })
    React.useLayoutEffect(() => {
      if (isInverted && containerRef && containerRef.current) {
        const containerEl = containerRef.current
        const handleWheel = e => {
          e.preventDefault()
          containerEl.scrollTop -= e.deltaY
        }
        containerEl.removeEventListener('wheel', handleWheel)
        containerEl.addEventListener('wheel', handleWheel, {
          passive: false,
        })
        return () => {
          containerEl.removeEventListener('wheel', handleWheel)
        }
      }
      return
    }, [isInverted])
    return (
      <ScrollableArea
        className={asStacked ? `${className} ${asStacked}` : className}
        height={controlOffset}
        isInverted={isInverted}
        onScroll={handleScroll}
        ref={containerRef}
        {...scrollDivProps}
      >
        {children}
      </ScrollableArea>
    )
  },
)

interface PaginatingProps {
  children: any
  className?: string
  controlOffset?: number
  fetchPolicy?: FetchPolicy
  forceRefetch?: boolean
  isInverted?: boolean
  lookupProp: string
  onScroll?: (e: any) => void
  query: any
  scrollDivProps?: any
  scrollToTopFn?: (prevData: any[] | null, nextData: any[] | null) => boolean
  variables?: any
  zeroStateMessage?: string
}

export const PagingSearchQuery: React.FC<PaginatingProps> = React.memo(
  ({
    children,
    className,
    controlOffset,
    fetchPolicy,
    forceRefetch,
    isInverted,
    lookupProp,
    onScroll,
    query,
    scrollDivProps,
    scrollToTopFn,
    variables,
    zeroStateMessage,
  }) => {
    const dataRef = React.useRef<any[] | null>(null)
    const {
      data,
      loading,
      loadNextPage,
      reachedLastPage,
      showZeroState,
    } = usePagingSearchQuery({
      fetchPolicy,
      forceRefetch,
      lookupProp,
      query,
      variables,
    })
    const scrollToTop = scrollToTopFn
      ? scrollToTopFn(dataRef.current, data)
      : false
    dataRef.current = data
    return (
      <PagingScrollableArea
        className={className}
        controlOffset={controlOffset}
        isInverted={isInverted}
        loadNextPage={loadNextPage}
        onScroll={onScroll}
        scrollDivProps={scrollDivProps}
        scrollToTop={scrollToTop}
      >
        <PaddedContent className="PaddedContent" isInverted={isInverted}>
          {showZeroState && (
            <div>
              <em>{zeroStateMessage}</em>
            </div>
          )}
          {data && children({ data, reachedLastPage })}
          {loading && <ContentLoader />}
        </PaddedContent>
      </PagingScrollableArea>
    )
  },
)

export default ScrollableContent
